import { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  BarChart3, TrendingUp, CheckCircle2, XCircle, Clock, Layers,
  AlertTriangle, RefreshCw, Loader2, ArrowUpRight, ArrowDownRight, Minus,
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { Link } from 'react-router-dom';
import { ClientFilterPill } from '../components/ClientFilterPill';

const RANGE_PRESETS = [
  { id: '7', label: 'Last 7 days', days: 7 },
  { id: '30', label: 'Last 30 days', days: 30 },
  { id: '90', label: 'Last 90 days', days: 90 },
];

const PLATFORM_COLOR = {
  linkedin: '#0a66c2',
  facebook: '#1877f2',
  instagram: '#e4405f',
  bluesky: '#0f172a',
  youtube: '#ff0000',
  tiktok: '#111111',
  blog: '#6366f1',
  email: '#10b981',
  unknown: '#94a3b8',
};

const fmtDate = (iso) =>
  new Date(iso + 'T00:00:00').toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });

const DeltaPill = ({ value }) => {
  if (value === null || value === undefined) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-zinc-400">
        <Minus className="w-3 h-3" /> —
      </span>
    );
  }
  const up = value > 0;
  const flat = value === 0;
  const Icon = flat ? Minus : up ? ArrowUpRight : ArrowDownRight;
  const color = flat
    ? 'text-zinc-400'
    : up
    ? 'text-emerald-600'
    : 'text-red-600';
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${color}`}>
      <Icon className="w-3 h-3" />
      {Math.abs(value)}%
    </span>
  );
};

const StatCard = ({ label, value, icon: Icon, accent = 'indigo', delta, sub, testid }) => {
  const accents = {
    indigo: 'bg-indigo-50 text-indigo-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    red: 'bg-red-50 text-red-600',
    amber: 'bg-amber-50 text-amber-600',
  };
  return (
    <Card className="border-zinc-200" data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-zinc-500">{label}</p>
            <p className="text-3xl font-bold text-zinc-900 mt-1">{value}</p>
            <div className="mt-1 flex items-center gap-2 text-xs text-zinc-500">
              {delta !== undefined && <DeltaPill value={delta} />}
              {sub && <span>{sub}</span>}
            </div>
          </div>
          <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${accents[accent]}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const SuccessRateBadge = ({ rate }) => {
  if (rate === null || rate === undefined) {
    return <Badge variant="outline" className="text-zinc-400">n/a</Badge>;
  }
  const color =
    rate >= 90 ? 'bg-emerald-100 text-emerald-700' :
    rate >= 70 ? 'bg-indigo-100 text-indigo-700' :
    rate >= 40 ? 'bg-amber-100 text-amber-700' :
                 'bg-red-100 text-red-700';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      {rate}%
    </span>
  );
};

export const AnalyticsPage = () => {
  const { api } = useAuth();
  const [rangeId, setRangeId] = useState('30');
  const [clientId, setClientId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchAnalytics = useMemo(
    () => async () => {
      const preset = RANGE_PRESETS.find((p) => p.id === rangeId) || RANGE_PRESETS[1];
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - preset.days);
      const params = new URLSearchParams({
        start: start.toISOString(),
        end: end.toISOString(),
      });
      if (clientId && clientId !== 'all') params.set('client_id', clientId);
      const res = await api.get(`/analytics?${params.toString()}`);
      return res.data;
    },
    [api, rangeId, clientId]
  );

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchAnalytics()
      .then((d) => alive && setData(d))
      .catch((err) => console.error('analytics fetch failed', err))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [fetchAnalytics]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const d = await fetchAnalytics();
      setData(d);
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-600 text-white flex items-center justify-center">
            <BarChart3 className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">
              Analytics
            </h1>
            <p className="text-zinc-500 mt-0.5 text-sm">
              Publishing performance across every platform and client.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ClientFilterPill
            value={clientId}
            onChange={setClientId}
          />
          <Select value={rangeId} onValueChange={setRangeId}>
            <SelectTrigger className="w-[160px]" data-testid="analytics-range">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGE_PRESETS.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="icon"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="analytics-refresh"
            aria-label="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      ) : !data ? (
        <Card className="border-zinc-200">
          <CardContent className="p-10 text-center">
            <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
            <p className="text-zinc-600">Couldn't load analytics. Try refreshing.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Stat cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              label="Total posts"
              value={data.summary.total}
              icon={Layers}
              accent="indigo"
              sub={`${data.window.days} day window`}
              testid="stat-total"
            />
            <StatCard
              label="Published"
              value={data.summary.published}
              icon={CheckCircle2}
              accent="emerald"
              delta={data.summary.delta_pct}
              sub={`prev ${data.summary.prev_published}`}
              testid="stat-published"
            />
            <StatCard
              label="Failed"
              value={data.summary.failed}
              icon={XCircle}
              accent="red"
              testid="stat-failed"
            />
            <StatCard
              label="Success rate"
              value={
                data.summary.success_rate === null
                  ? '—'
                  : `${data.summary.success_rate}%`
              }
              icon={TrendingUp}
              accent="amber"
              sub={`${data.summary.scheduled} still scheduled`}
              testid="stat-success-rate"
            />
          </div>

          {/* Trend chart */}
          <Card className="border-zinc-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-indigo-600" />
                Publishing trend
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-2">
              <div className="h-[260px] min-h-[260px]" data-testid="trend-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.trend} margin={{ top: 8, right: 16, bottom: 0, left: -8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fill: '#71717a' }}
                      tickFormatter={fmtDate}
                      interval="preserveStartEnd"
                    />
                    <YAxis tick={{ fontSize: 11, fill: '#71717a' }} allowDecimals={false} />
                    <Tooltip
                      labelFormatter={fmtDate}
                      contentStyle={{
                        borderRadius: 8,
                        border: '1px solid #e4e4e7',
                        fontSize: 12,
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="published" stroke="#10b981" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="scheduled" stroke="#6366f1" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="failed" stroke="#ef4444" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Side-by-side: by platform + by client */}
          <div className="grid lg:grid-cols-2 gap-4">
            <Card className="border-zinc-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Posts by platform</CardTitle>
              </CardHeader>
              <CardContent className="pt-2">
                {data.by_platform.length === 0 ? (
                  <p className="text-sm text-zinc-500 py-8 text-center">No posts in this window yet.</p>
                ) : (
                  <div className="space-y-3" data-testid="by-platform-list">
                    {data.by_platform.map((row) => {
                      const pct = Math.min(100, (row.total / (data.by_platform[0]?.total || 1)) * 100);
                      const color = PLATFORM_COLOR[row.platform?.toLowerCase()] || PLATFORM_COLOR.unknown;
                      return (
                        <div key={row.platform} data-testid={`platform-row-${row.platform}`}>
                          <div className="flex items-center justify-between text-sm">
                            <span className="font-medium text-zinc-800 capitalize">
                              {row.platform?.replace('_', ' ') || 'Unknown'}
                            </span>
                            <div className="flex items-center gap-2">
                              <span className="text-zinc-500">{row.total}</span>
                              <SuccessRateBadge rate={row.success_rate} />
                            </div>
                          </div>
                          <div className="mt-1.5 h-2 rounded-full bg-zinc-100 overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{ width: `${pct}%`, backgroundColor: color }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-zinc-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Posts by client</CardTitle>
              </CardHeader>
              <CardContent className="pt-2">
                {data.by_client.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-sm text-zinc-500">
                      No client-tagged posts in this window.
                    </p>
                    <Link to="/clients" className="inline-block mt-2 text-xs text-indigo-600 hover:text-indigo-700">
                      Set up clients →
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="by-client-list">
                    {data.by_client.map((row) => {
                      const pct = Math.min(100, (row.total / (data.by_client[0]?.total || 1)) * 100);
                      return (
                        <div key={row.client_id} data-testid={`client-row-${row.client_id}`}>
                          <div className="flex items-center justify-between text-sm">
                            <span className="flex items-center gap-2 font-medium text-zinc-800">
                              <span
                                className="w-2.5 h-2.5 rounded-full shrink-0"
                                style={{ backgroundColor: row.brand_color }}
                              />
                              <span className="truncate">{row.client_name}</span>
                            </span>
                            <div className="flex items-center gap-2">
                              <span className="text-zinc-500">{row.total}</span>
                              <SuccessRateBadge rate={row.success_rate} />
                            </div>
                          </div>
                          <div className="mt-1.5 h-2 rounded-full bg-zinc-100 overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{ width: `${pct}%`, backgroundColor: row.brand_color }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* By type + Top errors */}
          <div className="grid lg:grid-cols-2 gap-4">
            <Card className="border-zinc-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Content type mix</CardTitle>
              </CardHeader>
              <CardContent className="pt-2">
                {data.by_type.length === 0 ? (
                  <p className="text-sm text-zinc-500 py-8 text-center">Nothing to chart yet.</p>
                ) : (
                  <div className="h-[220px] min-h-[220px]" data-testid="by-type-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={data.by_type}
                        margin={{ top: 8, right: 16, bottom: 0, left: -8 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
                        <XAxis dataKey="post_type" tick={{ fontSize: 11, fill: '#71717a' }} />
                        <YAxis tick={{ fontSize: 11, fill: '#71717a' }} allowDecimals={false} />
                        <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e4e4e7', fontSize: 12 }} />
                        <Bar dataKey="total" fill="#6366f1" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-zinc-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  Top publish errors
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-2">
                {data.top_errors.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                    <p className="text-sm text-zinc-500">No publish errors — nice.</p>
                  </div>
                ) : (
                  <ul className="divide-y divide-zinc-100" data-testid="top-errors-list">
                    {data.top_errors.map((row, i) => (
                      <li key={i} className="py-2.5 flex items-start gap-3">
                        <span className="shrink-0 w-6 h-6 rounded-full bg-red-50 text-red-600 text-xs font-semibold flex items-center justify-center mt-0.5">
                          {row.count}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-zinc-800 leading-snug break-words">
                            {row.error}
                          </p>
                          {row.sample_post_ids?.length > 0 && (
                            <Link
                              to="/posts"
                              className="text-xs text-indigo-600 hover:text-indigo-700 mt-0.5 inline-flex items-center gap-1"
                            >
                              View affected posts →
                            </Link>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Footer strip */}
          <p className="text-xs text-zinc-500 flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" />
            Window: {new Date(data.window.start).toLocaleDateString()} →{' '}
            {new Date(data.window.end).toLocaleDateString()} · {data.window.days} days
          </p>
        </>
      )}
    </div>
  );
};

export default AnalyticsPage;
